var booleanStringParser = require('@/parsing/generated/boolean-string')
var browserQueryParser = require('@/parsing/generated/browser-query')
var numberedBooleanStringParser = require('@/parsing/generated/numbered-boolean-string')

const NumberedRE = /^[0-9]+[./]{0,1}[ \t]+\S+/
const SquareBracketFieldSpecifierRE = /\[(.*)\]$/
const polyglot = require('@iebh/polyglot').default

function flatten (r) {
  if (r.isGroup && r.children) {
    const x = []
    r.children.forEach(c => {
      flatten(c)
      let added = false
      if (
        c.isGroup &&
        !c.isLine &&
        c.operator === r.operator &&
        !c.fieldSpecifier
      ) {
        const group = c
        const allChildrenNegative = group.children.every(child => {
          return child.isNegative || false
        })
        const otherChildrenOfGroupNegative = r.children.every(child => {
          if (child === group) {
            return true // Ignore the current group being processed
          }
          return child.isNegative || false
        })
        const groupXorOtherChildrenNegative = (group.isNegative || false) ^ otherChildrenOfGroupNegative
        if ((!groupXorOtherChildrenNegative && !allChildrenNegative) ||
            (groupXorOtherChildrenNegative && allChildrenNegative)) {
          x.push(...c.children)
          added = true
        }
      }
      if (!added) {
        x.push(c)
      }
    })
    r.children = x
  }
}

function processFieldSpecifiers (r) {
  if (r.isGroup && r.children) {
    r.children.map(c => processFieldSpecifiers(c))
  }
  if (r.text) {
    try {
      if (SquareBracketFieldSpecifierRE.test(r.text)) {
        const squareFieldSpecifier = SquareBracketFieldSpecifierRE.exec(r.text)
        if (squareFieldSpecifier) {
          r.text = r.text.replace(squareFieldSpecifier[0], '')
          r.fieldSpecifier = squareFieldSpecifier[1]
        }
      } else {
        const parts = r.text.split(':')
        if (parts.length > 1) {
          r.fieldSpecifier = parts.shift()
          r.text = parts.join(':')
          if (r.text.startsWith('"')) {
            r.text = r.text.substring(1)
          }
          if (r.text.endsWith('"')) {
            r.text = r.text.substring(0, r.text.length - 1)
          }
        }
      }
    } catch (e) {
      console.warn('exception', e)
    }
  }
}

function polyglotTreeSize (tree) {
  let count = 0
  tree.forEach(node => {
    if (Array.isArray(node)) {
      count += polyglotTreeSize(node)
    } else {
      switch (node.type) {
        case 'line':
        case 'group':
        case 'ref':
          count += polyglotTreeSize(node.nodes)
          break
        case 'phrase':
        case 'mesh':
        case 'meshMajor':
          count++
          break
      }
    }
  })
  return count
}

const polyglotOperatorMap = {
  'joinOr': 'OR',
  'joinAnd': 'AND',
  'joinNear': 'ADJ',
  'joinNot': 'AND'
}
function mapPolyglotOperatorTo2dSearch (operator, currentProximity) {
  const polyglotOperator = polyglotOperatorMap[operator]
  if (polyglotOperator && currentProximity !== undefined) {
    return `${polyglotOperator}${currentProximity}`
  } else {
    return polyglotOperator || 'AND'
  }
}

function removeSurroundingCharacters (surround, haystack) {
  if (haystack && haystack.length > 2 && haystack[0] === surround &&
      haystack[haystack.length - 1] === surround) {
    return haystack.substring(1, haystack.length - 1)
  } else {
    return haystack
  }
}

function polyglotTidyContent (text) {
  return removeSurroundingCharacters('\'', text).trim()
}

function polyglotFieldSpecifierToString (field) {
  switch (field) {
    case 'Text word': return 'tw'
    case 'All fields': return 'all'
    case 'Abstract': return 'ab'
    case 'Author': return 'au'
    case 'Conflict of interest': return 'cois'
    case 'Language': return 'la'
    case 'Title/abstract search': return 'tiab'
    case 'Title search': return 'ti'
    case 'Keyword Field (.kf.)': return 'ot'
    case 'Title, abstract, keyword (.ti,ab,kf.)': return 'tiab'
    case 'Affiliation': return 'AD'
    case 'Article identifier': return 'AID'
    case 'Author IDs, such as ORCiD': return 'AUID'
    case 'Book citation field': return 'BOOK'
    case 'Corporate author': return 'CN'
    case 'ER/RN numbers': return 'RN'
    case 'Editors for book or chapter citation': return 'ED'
    case 'First personal author name in a citation': return '1AU'
    case 'Full authors name': return 'FAU'
    case 'Full investigators name': return 'FIR'
    case 'Grant and financial support numbers': return 'GR'
    case 'Names of principal investigator(s) or collaborators': return 'IR'
    case 'ISBN for book or chapter': return 'ISBN'
    case 'Journal issue numbers': return 'IP'
    case 'Journal title, abbreviation or ISSN': return 'TA'
    case 'Last author\'s name in citation': return 'LASTAU'
    case 'DOI or publisher ID for online articles': return 'LID'
    case 'NLM ID for journal': return 'JID'
    case 'The first number the article appears on': return 'PG'
    case 'Retrieves results where the name is the subject, rather than author': return 'PS'
    case 'Substances known to have a pharmacological action': return 'PA'
    case 'Journals country of publication': return 'PL'
    case 'PubMed indentifier': return 'PMID'
    case 'Type of material the article represents, e.g. clinical trial': return 'pt'
    case 'Publisher name, for NCBI BookShelf citations': return 'PUBN'
    case 'Secondary source databanks and accession numbers, e.g., GenBank, GEO, PubChem, ClinicalTrials.gov, ISRCTN': return 'SI'
    case 'The Journal Subset (SB) field identifies the subset for MEDLINE records from certain journal lists or records on specialized topics': return 'SB'
    case 'Includes chemical, protocol, disease or organism Tests': return 'nm'
    case 'Includes all words and numbers in the title, abstract, other abstract, MeSH Tests, MeSH Subheadings, Publication Types, Substance Names, Personal Name as Subject, Corporate Author, Secondary Source, Comment/Correction Notes, and Other Tests (see Other Test [OT] above) typically non-MeSH subject Tests (keywords)': return 'TW'
    case 'Words and numbers of original, non-English title': return 'TT'
    case 'Volume number of published article': return 'VI'
    //
    case 'MeSH Major Topic search (Not exploded)': return 'majr:NoExp'
    case 'MeSH Major Topic search (exploded)': return 'majr'
    case 'Mesh search (Not exploded)': return 'mh:NoExp'
    case 'Mesh search (exploded)': return 'mh'
    //
    case 'title': return 'ti'
    case 'title+abstract': return 'tiab'
    case 'title+abstract+tw': return 'tiab'
    case 'title+abstract+other': return 'tw'
    case 'abstract': return 'ab'
    case 'floatingSubheading': return 'sh'
    case 'mesh': return 'sh'
    case 'meshMajor': return 'sh'
    case 'substance': return 'nm'
    case 'publicationType': return 'pt'
    case 'author': return 'kf'
    default: return null
  }
}

function determineFieldSpecifier (treeElement) {
  if (treeElement.field) {
    if (treeElement.type === 'meshTranslation') {
      return 'sh'
    } else {
      return polyglotFieldSpecifierToString(treeElement.field)
    }
  } else if (treeElement.type === 'mesh') {
    return `mh${treeElement.recurse ? '' : ':NoExp'}`
  } else if (treeElement.type === 'meshMajor') {
    return `majr${treeElement.recurse ? '' : ':NoExp'}`
  } else {
    return null
  }
}

function polyglotFormSingleTerm (treeElement) {
  if (!treeElement) {
    return null
  }
  const r = {}
  if (treeElement.content) {
    r.text = polyglotTidyContent(treeElement.content)
  } else if (treeElement.type === 'meshTranslation' &&
      treeElement.field) {
    r.text = polyglotTidyContent(treeElement.field)
  }
  const fieldSpecifier = determineFieldSpecifier(treeElement)
  if (fieldSpecifier) {
    r.fieldSpecifier = fieldSpecifier
  }
  return r
}

function polyglotAddSingleTerm (treeElement, previousElement, isNot) {
  let newTerm = null
  if (treeElement.content || (treeElement.type === 'meshTranslation' &&
      treeElement.field)) {
    newTerm = polyglotFormSingleTerm(treeElement)
  }
  if (newTerm) {
    if (isNot || (previousElement && previousElement.type === 'joinNot')) {
      newTerm.isNegative = true
    }
  }
  return newTerm
}

function polyglotTreeTo2dSearch (tree) {
  let q = null
  if (tree && !Array.isArray(tree) && tree.nodes) {
    q = polyglotFormSingleTerm(tree.nodes[0])
  } else if (tree && Array.isArray(tree) && tree.length >= 1) {
    let currentOperator = tree.length > 1 ? tree[1].type : null
    let currentProximity = tree.length > 1 ? tree[1].proximity : null
    const currentChildren = []
    q = {
      isGroup: true,
      operator: mapPolyglotOperatorTo2dSearch(currentOperator, currentProximity),
      children: currentChildren
    }
    let i = 0
    let isNot = false
    while (i < tree.length) {
      if (tree[i].type === 'joinNot') {
        isNot = true
        i++
      }
      if (tree[i].type === 'phrase' || tree[i].type === 'mesh' ||
          tree[i].type === 'meshMajor' || tree[i].type === 'meshTranslation') {
        const x = polyglotAddSingleTerm(tree[i], null, isNot)
        if (x) {
          currentChildren.push(x)
        }
      }
      if (tree[i].type === 'group' || tree[i].type === 'line') {
        const x = polyglotTreeTo2dSearch(tree[i].nodes)
        const fieldSpecifier = determineFieldSpecifier(tree[i])
        if (isNot) {
          x.isNegative = true
        }
        if (fieldSpecifier) {
          x.fieldSpecifier = fieldSpecifier
        }
        currentChildren.push((x.children && x.children.length === 1)
          ? x.children[0] : x)
      }
      if (tree[i].type === 'ref') {
        const refCond = tree[i].cond
        if (refCond && refCond !== '') {
          q.operator = tree[i].cond
        }
        tree[i].nodes.forEach(treeElement => {
          if (treeElement !== undefined) {
            if (treeElement.length === 1 &&
              (treeElement[0].type === 'phrase' ||
                treeElement[0].type === 'mesh' ||
                treeElement[0].type === 'meshMajor' ||
                tree[i].type === 'meshTranslation')
            ) {
              const x = polyglotAddSingleTerm(treeElement[0], null, isNot)
              if (x) {
                currentChildren.push(x)
              }
            } else {
              const x = polyglotTreeTo2dSearch(treeElement)
              const fieldSpecifier = determineFieldSpecifier(tree[i])
              if (isNot) {
                x.isNegative = true
              }
              if (fieldSpecifier) {
                x.fieldSpecifier = fieldSpecifier
              }
              currentChildren.push((x.children && x.children.length === 1)
                ? x.children[0] : x)
            }
          }
        })
      }
      i++
      isNot = false
    }
  }
  return (q.children && q.children.length === 1)
    ? q.children[0] : q
}

function polyglotParse (text) {
  let p = null
  try {
    p = polyglot.translateAll(text, {
      groupLines: false,
      groupLinesAlways: true,
      removeNumbering: false,
      replaceWildcards: true,
      transposeLines: true,
      html: false
    })
  } catch (e) {
    console.warn(`Polyglot translate failed - ${e}`)
  }
  let t = p.lexicalTreeJSON
  // console.log('t:', t)
  if (t && Array.isArray(t)) {
    if (t.length <= 0) {
      return ''
    } else if (t.length === 1) {
      return t[0]
    } else {
      const lines = t.filter(x => x.type === 'line')
      if (lines.length > 1 && lines[0].isNumbered) {
        let maxCount = 0
        let biggestLine = null
        t.forEach(node => {
          if (node.type === 'line') {
            const subTreeCount = polyglotTreeSize(node.nodes)
            if (subTreeCount > maxCount) {
              maxCount = subTreeCount
              biggestLine = node
            }
          }
        })
        return biggestLine
      } else {
        return t
      }
    }
  }
  return t
}

function parse (text, querySyntax) {
  let trimmed = text.replace(/;{0,1}["\u201c\u201d];{0,1}/g, '"') // Replaced double quote - semi-colon pair with just double quote
    .split('\n').map(x => x.trim()).join('\n').trim()
  let r = null
  if (querySyntax === 'web') {
    if (NumberedRE.test(trimmed)) {
      r = numberedBooleanStringParser.parse(trimmed)
    } else {
      try {
        r = booleanStringParser.parse(trimmed)
      } catch (e) {
        try {
          r = browserQueryParser.parse(trimmed)
        } catch (e2) {
          throw e
        }
      }
    }
  } else {
    let pp = polyglotParse(trimmed)
    r = polyglotTreeTo2dSearch(Array.isArray(pp) ? pp : pp.nodes)
  }
  flatten(r)
  if (r !== null) {
    processFieldSpecifiers(r)
  }
  return r
}

export { parse }



// WEBPACK FOOTER //
// ./src/parsing/parsing.js